import { logError } from '../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * Notes API Module
 * Handles all note-related API calls for the Notes feature
 */

/**
 * Fetch all notes for the current user
 * GET /api/notes
 */
export async function fetchNotes(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.filter) queryParams.append('filter', params.filter);
    if (params.tag) queryParams.append('tag', params.tag);
    if (params.search) queryParams.append('search', params.search);
    if (params.pinned !== undefined) queryParams.append('pinned', params.pinned);
    if (params.archived !== undefined) queryParams.append('archived', params.archived);
    if (params.visibility) queryParams.append('visibility', params.visibility);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/api/notes${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch notes failed:", response.status, errorText);
        throw new Error("Failed to fetch notes. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Fetch a single note by ID
 * GET /api/notes/{note_id}
 */
export async function fetchNoteById(noteId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch note failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Note not found.");
        }
        throw new Error("Failed to fetch note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Create a new note
 * POST /api/notes
 */
export async function createNote(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({
            content: payload.content,
            visibility: payload.visibility || 'PRIVATE',
            pinned: payload.pinned || false,
            attachments: payload.attachments || []
        })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Create note failed:", response.status, errorText);
        throw new Error("Failed to create note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Update an existing note
 * PATCH /api/notes/{note_id}
 */
export async function updateNote(noteId, updates) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Update note failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Note not found.");
        }
        throw new Error("Failed to update note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Delete a note
 * DELETE /api/notes/{note_id}
 */
export async function deleteNote(noteId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}`, {
        method: "DELETE"
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete note failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Note not found.");
        }
        throw new Error("Failed to delete note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Pin/Unpin a note
 */
export async function toggleNotePin(noteId, pinned) {
    return updateNote(noteId, { pinned });
}

/**
 * Archive/Unarchive a note
 */
export async function toggleNoteArchive(noteId, archived) {
    return updateNote(noteId, { archived });
}

/**
 * Fetch all unique tags used in notes
 * GET /api/notes/tags
 */
export async function fetchNoteTags() {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/tags`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch tags failed:", response.status, errorText);
        throw new Error("Failed to fetch tags. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Fetch note statistics for the current user
 * GET /api/notes/stats
 */
export async function fetchNoteStats() {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/stats`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch stats failed:", response.status, errorText);
        throw new Error("Failed to fetch statistics. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Search notes by content
 * GET /api/notes/search?q={query}
 */
export async function searchNotes(query, options = {}) {
    const params = new URLSearchParams({ q: query });
    if (options.limit) params.append('limit', options.limit);
    if (options.tag) params.append('tag', options.tag);

    const response = await fetchWithAuth(`${BASE_URL}/api/notes/search?${params}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Search notes failed:", response.status, errorText);
        throw new Error("Failed to search notes. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Upload an attachment for a note
 * POST /api/notes/attachments
 */
export async function uploadNoteAttachment(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${BASE_URL}/api/notes/attachments`, {
        method: "POST",
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Upload attachment failed:", response.status, errorText);
        throw new Error("Failed to upload attachment. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}
